import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BdDunningTypeBaseComponent} from '../base/bd-dunning-type-base.component';
import {DUNNING_TYPE_NODE_VIEW_CONFIG} from '../../shared/dunning-type-node-view-config';
import {
    ConfigNodeAddEvent,
    ConfigNodeEditEvent,
    ConfigNodeSelectEvent
} from 'features/core/shared/components/mvs-config-node-view/data/config-node-view.interfaces';
import {MvsUiObjectService} from 'features/core/shared/object/service/mvs-ui-object.service';
import {MvsCrudModeEnum} from 'features/core/shared/service/crud/mvs-crud-mode.enum';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';

/**
 * Full view of the DunningType object component (`uiMode = 'full'`), layer 3 of the three-layer OBC
 * pattern.
 *
 * It inherits {@link BdDunningTypeBaseComponent#navigationItems} and
 * {@link BdDunningTypeBaseComponent#childTables} and renders EXACTLY ONE of them per active
 * navigation point, so only one child query fires per view instead of ten at once.
 *
 * Beyond the plain child tables it owns the graphical node view: the canvas is bound here and the
 * four canvas events (select / add / edit / busy) are handled here, because they open object
 * dialogs and therefore need `MvsUiObjectService` — a dependency the root component no longer has.
 *
 * Reference implementation followed: `features/feature-prov/cp/component/object-components/
 * cp-provision-type-object/view/full/cp-provision-type-full.component.ts`.
 */
@Component({
    selector: 'bd-dunning-type-full',
    templateUrl: './bd-dunning-type-full.component.html',
    styleUrls: ['./bd-dunning-type-full.component.scss'],
    standalone: false
})
export class BdDunningTypeFullComponent extends BdDunningTypeBaseComponent implements OnInit {

    /**
     * Static canvas configuration of the node view (nodes, foreign keys, icons, display fields).
     *
     * The canvas runs without any custom node template: the config's `displayField` plus the
     * built-in terminal title render every node correctly, including truncation and tooltip.
     */
    readonly dunningTypeNodeViewConfig = DUNNING_TYPE_NODE_VIEW_CONFIG;

    /**
     * Loading state reported by the node view while it fetches its child entities. It lives here
     * rather than on the root component, which no longer renders the canvas and therefore has no
     * business knowing about it. The canvas draws its own skeletons, so the flag is state only —
     * the hook exists so a surrounding busy indicator can be added without touching the canvas.
     */
    busy: boolean = false;

    constructor(
        protected route: ActivatedRoute,
        protected mvsUiObjectService: MvsUiObjectService
    ) {
        super(route);
    }

    ngOnInit() {
        super.ngOnInit();
    }

    // =========================================================================
    // MvsConfigNodeViewComponent event handlers
    // =========================================================================

    /**
     * A node was clicked — open the entity read-only.
     */
    handleNodeSelect(event: ConfigNodeSelectEvent): void {
        const {entityId, entityType} = event;

        if (!entityId || !entityType) {
            return;
        }

        this.mvsUiObjectService.openObjectViaDialog(
            null,                  // importObjectContext
            entityType,            // e.g. 'bd.DunningTypeLevel'
            entityId,              // existing id
            null,                  // defaultCreateDto (not needed for read)
            true,                  // readonly
            false,                 // disabled
            null,                  // onChangeFunction (no callback for read-only)
            MvsCrudModeEnum.read
        );
    }

    /**
     * The "+" of a node group was clicked — open the create dialog with the parent FK pre-filled.
     */
    handleAddChild(event: ConfigNodeAddEvent): void {
        if (!event.entityType) {
            return;
        }

        this.mvsUiObjectService.openObjectViaDialog(
            null,
            event.entityType,
            0,                     // 0 = create mode
            this.createDefaultDtoForEntityType(event.entityType),
            false,                 // readonly
            false,                 // disabled
            () => this.onDialogSave(),
            MvsCrudModeEnum.create
        );
    }

    /**
     * The edit pencil of a node was clicked — open the entity in the edit dialog.
     */
    handleNodeEdit(event: ConfigNodeEditEvent): void {
        if (!event.entityId || !event.entityType) {
            return;
        }

        this.mvsUiObjectService.openObjectViaDialog(
            null,
            event.entityType,
            event.entityId,        // existing id
            null,                  // defaultCreateDto (loaded from the API)
            false,
            false,
            () => this.onDialogSave(),
            MvsCrudModeEnum.update
        );
    }

    /**
     * The node view reports its loading state.
     */
    handleNodeBusyChange(busy: boolean): void {
        this.busy = busy;
    }

    /**
     * Child entities whose `entityStatus` is business relevant, i.e. the two reason lists.
     *
     * The backend evaluates ACTIVE reasons ONLY (`BdDunningStornoReasonService:86`,
     * `BdDunningPauseService:609/641`). A row left at the persistence default `entityStatus = 0`
     * (draft) is therefore visible in the UI, looks configured, and has no effect whatsoever — the
     * exact defect the removed custom editors produced.
     *
     * The same two entity types are flagged `defaultActiveStatus` in the child-table descriptors, so
     * BOTH create paths — the canvas "+" here and the table's "+" — pre-fill the identical value and
     * cannot drift apart. The pre-fill is the ONLY mechanism on either path: the status field stays
     * fully editable and is deliberately NOT restricted through a value-list filter, because such a
     * filter also applies to the edit dialog and would hide the current value of legacy draft rows.
     */
    private static readonly cActiveStatusEntityTypes: string[] = [
        'bd.DunningTypeTerminationReason',
        'bd.DunningTypePauseReason'
    ];

    /**
     * Default DTO for a node created from the canvas.
     *
     * Every child entity of the dunning type carries the very same mandatory parent FK
     * `dunningTypeDtoId`, so the FK needs no branching at all — the former per-entity switch listed
     * nine identical cases and a default that silently produced an EMPTY dto for the tenth, which
     * is why the new `pauseReasons` node would have failed on its `@NotNull` FK.
     *
     * The entity type still matters for one thing: the status pre-fill of the two reason lists
     * (see {@link cActiveStatusEntityTypes}).
     */
    private createDefaultDtoForEntityType(entityType: string): any {
        const dto: any = {dunningTypeDtoId: this.objectIdentifier.objectId};

        if (BdDunningTypeFullComponent.cActiveStatusEntityTypes.includes(entityType)) {
            dto.entityStatus = EntityStatusEnum.active;
        }

        return dto;
    }

    /**
     * Callback after a dialog save. The node view keeps its own cache in sync, so there is nothing
     * to reload here; the hook stays as the documented place for additional refresh logic.
     */
    private onDialogSave(): void {
    }

}
